import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Link2, Unlink, Send, Clock, CheckCircle2, XCircle, AlertTriangle,
  RefreshCw, Loader2, ExternalLink, Calendar, RotateCcw, Trash2, Key
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

export default function XConnectionPage() {
  const [searchParams] = useSearchParams();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState({ active: false, pending_jobs: 0 });

  // PIN flow state
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pin, setPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  // Publish state
  const [tweetText, setTweetText] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);

  // Actions
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const loadData = useCallback(() => {
    api.get('/social/x/status').then(r => setConnection(r.data)).catch(() => {});
    api.get('/social/x/posts?limit=20').then(r => setPosts(r.data)).catch(() => {});
    api.get('/social/x/logs?limit=30').then(r => setLogs(r.data)).catch(() => {});
    api.get('/social/x/scheduler/status').then(r => setSchedulerStatus(r.data)).catch(() => {});
    setLoading(false);
  }, []);

  useEffect(() => {
    const connected = searchParams.get('connected');
    const username = searchParams.get('username');
    const error = searchParams.get('error');
    if (connected === 'true' && username) {
      toast.success(`@${username} hesab\u0131 ba\u015far\u0131yla ba\u011fland\u0131!`);
    } else if (error) {
      toast.error(`Ba\u011flant\u0131 hatas\u0131: ${error}`);
    }
    loadData();
  }, [searchParams, loadData]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.get('/social/x/connect');
      if (r.data.flow_type === 'pin') {
        // PIN-based flow: open auth URL in new tab, show PIN dialog
        window.open(r.data.auth_url, '_blank');
        setShowPinDialog(true);
      } else {
        // Callback-based flow: redirect
        window.location.href = r.data.auth_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ba\u011flant\u0131 ba\u015flat\u0131lamad\u0131');
    }
    setConnecting(false);
  };

  const handlePinSubmit = async () => {
    if (!pin.trim()) { toast.error('PIN kodunu girin'); return; }
    setPinLoading(true);
    try {
      const r = await api.post('/social/x/verify-pin', { pin: pin.trim() });
      toast.success(`@${r.data.username} hesab\u0131 ba\u015far\u0131yla ba\u011fland\u0131!`);
      setShowPinDialog(false);
      setPin('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PIN do\u011frulanamad\u0131');
    }
    setPinLoading(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post('/social/x/disconnect');
      toast.success('X hesab\u0131 ba\u011flant\u0131s\u0131 kesildi');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
    setDisconnecting(false);
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const r = await api.post('/social/x/verify');
      if (r.data.valid) {
        toast.success('Ba\u011flant\u0131 aktif ve ge\u00e7erli');
      } else {
        toast.error('Ba\u011flant\u0131 ge\u00e7ersiz, yeniden ba\u011flanman\u0131z gerekiyor');
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Do\u011frulama hatas\u0131');
    }
    setVerifying(false);
  };

  const handlePublish = async () => {
    if (!tweetText.trim()) { toast.error('Tweet metni bo\u015f olamaz'); return; }
    if (tweetText.length > 280) { toast.error('Tweet metni 280 karakteri a\u015famaz'); return; }
    setPublishing(true);
    try {
      const r = await api.post('/social/x/publish', { text: tweetText });
      if (r.data.success) {
        toast.success('Tweet ba\u015far\u0131yla yay\u0131nland\u0131!');
        setTweetText('');
        loadData();
      } else {
        toast.error(`Yay\u0131nlama hatas\u0131: ${r.data.error}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yay\u0131nlama hatas\u0131');
    }
    setPublishing(false);
  };

  const handleSchedule = async () => {
    if (!tweetText.trim()) { toast.error('Tweet metni bo\u015f olamaz'); return; }
    if (!scheduleDate || !scheduleTime) { toast.error('Tarih ve saat se\u00e7in'); return; }
    setScheduling(true);
    try {
      const scheduled_at = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      await api.post('/social/x/schedule', { text: tweetText, scheduled_at });
      toast.success('G\u00f6nderi planland\u0131!');
      setTweetText('');
      setShowSchedule(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Planlama hatas\u0131');
    }
    setScheduling(false);
  };

  const handleCancelJob = async (jobId) => {
    try {
      await api.post(`/social/x/jobs/${jobId}/cancel`);
      toast.success('\u0130ptal edildi');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
  };

  const toggleScheduler = async () => {
    try {
      const endpoint = schedulerStatus.active ? '/social/x/scheduler/stop' : '/social/x/scheduler/start';
      const r = await api.post(endpoint);
      toast.success(r.data.message);
      setSchedulerStatus({ ...schedulerStatus, active: r.data.active });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
  };

  const statusBadge = (s) => {
    const map = {
      published: { text: 'Yay\u0131nland\u0131', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
      failed: { text: 'Ba\u015far\u0131s\u0131z', cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
      pending: { text: 'Bekliyor', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
      processing: { text: '\u0130\u015fleniyor', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      cancelled: { text: '\u0130ptal', cls: 'bg-[#525252]/10 text-[#737373] border-[#525252]/20' },
    };
    const m = map[s] || { text: s, cls: 'bg-[#262626] text-[#A3A3A3]' };
    return <Badge variant="outline" className={`text-xs ${m.cls}`}>{m.text}</Badge>;
  };

  const isConnected = connection?.connected;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#525252]" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="x-connection-page" className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-black flex items-center justify-center text-white font-bold text-xl border border-[#262626]">X</span>
            X (Twitter) Entegrasyonu
          </h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">
            Hesab{'\u0131'}n{'\u0131'}z{'\u0131'} ba{'\u011f'}lay{'\u0131'}n, tweet at{'\u0131'}n ve g{'\u00f6'}nderilerinizi y{'\u00f6'}netin
          </p>
        </div>
      </div>

      {/* Connection Card */}
      <Card className={`border ${isConnected ? 'bg-[#141414] border-green-500/20' : 'bg-[#141414] border-[#262626]'}`} data-testid="x-connection-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {isConnected && connection.profile_image_url ? (
                <img src={connection.profile_image_url} alt="" className="w-14 h-14 rounded-full border-2 border-[#262626]" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#262626] flex items-center justify-center">
                  <span className="text-2xl font-bold text-[#525252]">X</span>
                </div>
              )}
              <div>
                {isConnected ? (
                  <>
                    <p className="text-white font-ibm font-semibold text-lg">@{connection.username}</p>
                    <p className="text-[#A3A3A3] font-ibm text-sm">{connection.display_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/20">
                        <CheckCircle2 size={10} className="mr-1" /> Ba{'\u011f'}l{'\u0131'}
                      </Badge>
                      <span className="text-[10px] text-[#525252] font-jetbrains">
                        {connection.connected_at && new Date(connection.connected_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-white font-ibm font-semibold">X Hesab{'\u0131'} Ba{'\u011f'}l{'\u0131'} De{'\u011f'}il</p>
                    <p className="text-[#A3A3A3] font-ibm text-sm">Hesab{'\u0131'}n{'\u0131'}z{'\u0131'} ba{'\u011f'}layarak tweet atabilir ve g{'\u00f6'}nderilerinizi y{'\u00f6'}netebilirsiniz</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Button onClick={handleVerify} disabled={verifying} variant="outline" size="sm"
                    className="border-[#262626] text-[#A3A3A3] hover:text-white h-9" data-testid="verify-btn">
                    {verifying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                    Do{'\u011f'}rula
                  </Button>
                  <Button onClick={handleDisconnect} disabled={disconnecting} variant="outline" size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9" data-testid="disconnect-btn">
                    {disconnecting ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} className="mr-1" />}
                    Ba{'\u011f'}lant{'\u0131'}y{'\u0131'} Kes
                  </Button>
                </>
              ) : (
                <Button onClick={handleConnect} disabled={connecting}
                  className="bg-white text-black hover:bg-[#E5E5E5] h-10 px-6 font-medium" data-testid="connect-btn">
                  {connecting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Link2 size={16} className="mr-2" />}
                  X Hesab{'\u0131'}n{'\u0131'} Ba{'\u011f'}la
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Publish / History / Logs */}
      {isConnected && (
        <Tabs defaultValue="publish" className="space-y-4">
          <TabsList className="bg-[#141414] border border-[#262626]">
            <TabsTrigger value="publish" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm">
              <Send size={14} className="mr-1.5" /> G{'\u00f6'}nderi Olu{'\u015f'}tur
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm">
              <Clock size={14} className="mr-1.5" /> Ge{'\u00e7'}mi{'\u015f'}
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-sm">
              <RotateCcw size={14} className="mr-1.5" /> {'\u0130'}{'\u015f'}lem Ge{'\u00e7'}mi{'\u015f'}i
            </TabsTrigger>
          </TabsList>

          {/* Publish Tab */}
          <TabsContent value="publish">
            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader>
                <CardTitle className="font-outfit text-lg text-white">Tweet Olu{'\u015f'}tur</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#A3A3A3] font-ibm text-sm">Tweet Metni</Label>
                    <span className={`text-xs font-jetbrains ${tweetText.length > 280 ? 'text-red-400' : tweetText.length > 250 ? 'text-yellow-400' : 'text-[#525252]'}`}>
                      {tweetText.length}/280
                    </span>
                  </div>
                  <textarea
                    value={tweetText}
                    onChange={e => setTweetText(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-lg p-4 min-h-[140px] text-sm font-ibm focus:outline-none focus:border-[#404040] resize-none"
                    placeholder="Tweet metninizi yaz&#305;n..."
                    maxLength={280}
                    data-testid="tweet-textarea"
                  />
                </div>

                <div className="flex items-center justify-between flex-wrap gap-3">
                  <p className="text-xs text-[#525252] font-ibm flex items-center gap-1">
                    <span className="w-5 h-5 rounded-full bg-black flex items-center justify-center text-white text-[10px] font-bold border border-[#262626]">X</span>
                    @{connection?.username} olarak payla{'\u015f'}{'\u0131'}lacak
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowSchedule(true)}
                      disabled={!tweetText.trim() || tweetText.length > 280}
                      variant="outline"
                      className="border-[#262626] text-[#A3A3A3] hover:text-white h-9"
                      data-testid="schedule-btn"
                    >
                      <Calendar size={14} className="mr-1" /> Zamanla
                    </Button>
                    <Button
                      onClick={handlePublish}
                      disabled={publishing || !tweetText.trim() || tweetText.length > 280}
                      className="bg-white text-black hover:bg-[#E5E5E5] h-9 px-5"
                      data-testid="publish-btn"
                    >
                      {publishing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                      Hemen Payla{'\u015f'}
                    </Button>
                  </div>
                </div>

                {/* Scheduler Control */}
                <div className="pt-4 border-t border-[#1A1A1A]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white font-ibm">Zamanlanm{'\u0131'}{'\u015f'} G{'\u00f6'}nderi Zamanlayıcısı</p>
                      <p className="text-xs text-[#525252] font-ibm">
                        {schedulerStatus.active ? `Aktif - ${schedulerStatus.pending_jobs} bekleyen g\u00f6nderi` : 'Durdurulmu\u015f'}
                      </p>
                    </div>
                    <Button onClick={toggleScheduler} variant="outline" size="sm"
                      className={schedulerStatus.active ? 'border-red-500/30 text-red-400' : 'border-green-500/30 text-green-400'}
                      data-testid="toggle-scheduler-btn">
                      {schedulerStatus.active ? 'Durdur' : 'Ba{\'\\u015f\'}lat'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg text-white">G{'\u00f6'}nderi Ge{'\u00e7'}mi{'\u015f'}i</CardTitle>
                  <Button onClick={loadData} variant="ghost" size="sm" className="text-[#525252] hover:text-white">
                    <RefreshCw size={14} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {posts.length === 0 ? (
                  <p className="text-[#737373] font-ibm text-sm py-8 text-center">Hen{'\u00fc'}z g{'\u00f6'}nderi yok</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#262626]">
                        <TableHead className="text-[#A3A3A3] font-ibm">Metin</TableHead>
                        <TableHead className="text-[#A3A3A3] font-ibm">Durum</TableHead>
                        <TableHead className="text-[#A3A3A3] font-ibm">Planland{'\u0131'}</TableHead>
                        <TableHead className="text-[#A3A3A3] font-ibm">Yay{'\u0131'}nland{'\u0131'}</TableHead>
                        <TableHead className="text-[#A3A3A3] font-ibm">Link</TableHead>
                        <TableHead className="text-[#A3A3A3] font-ibm"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posts.map(p => (
                        <TableRow key={p.id} className="border-[#262626] hover:bg-white/5">
                          <TableCell className="text-white font-ibm text-sm max-w-[250px] truncate">{p.text_content}</TableCell>
                          <TableCell>{statusBadge(p.status)}</TableCell>
                          <TableCell className="text-[#525252] font-jetbrains text-xs">
                            {p.scheduled_at ? new Date(p.scheduled_at).toLocaleString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell className="text-[#525252] font-jetbrains text-xs">
                            {p.published_at ? new Date(p.published_at).toLocaleString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>
                            {p.remote_post_url ? (
                              <a href={p.remote_post_url} target="_blank" rel="noopener noreferrer" className="text-[#1DA1F2] hover:text-white">
                                <ExternalLink size={14} />
                              </a>
                            ) : p.error_message ? (
                              <span className="text-xs text-red-400 max-w-[150px] truncate block" title={p.error_message}>
                                {p.error_message.slice(0, 40)}...
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {p.status === 'pending' && (
                              <Button onClick={() => handleCancelJob(p.id)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#525252] hover:text-red-400">
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader>
                <CardTitle className="font-outfit text-lg text-white">{'\u0130'}{'\u015f'}lem Ge{'\u00e7'}mi{'\u015f'}i</CardTitle>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <p className="text-[#737373] font-ibm text-sm py-8 text-center">Hen{'\u00fc'}z i{'\u015f'}lem yok</p>
                ) : (
                  <div className="space-y-2">
                    {logs.map(l => (
                      <div key={l.id} className="flex items-start gap-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]">
                        {l.status === 'success' ? <CheckCircle2 size={16} className="text-green-400 mt-0.5 shrink-0" /> :
                         l.status === 'failed' ? <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" /> :
                         <AlertTriangle size={16} className="text-yellow-400 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] bg-[#262626] text-[#A3A3A3] border-[#404040]">{l.action_type}</Badge>
                            <span className="text-[10px] text-[#525252] font-jetbrains">{new Date(l.created_at).toLocaleString('tr-TR')}</span>
                          </div>
                          <p className="text-sm text-white font-ibm mt-1">{l.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* PIN Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Key size={20} /> X Yetkilendirme PIN'i
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] p-4">
              <ol className="space-y-2 text-sm text-[#A3A3A3] font-ibm list-decimal list-inside">
                <li>A{'\u00e7'}{'\u0131'}lan X sayfas{'\u0131'}nda hesab{'\u0131'}n{'\u0131'}za giri{'\u015f'} yap{'\u0131'}n</li>
                <li>Uygulamay{'\u0131'} yetkilendirin ("Authorize app")</li>
                <li>G{'\u00f6'}r{'\u00fc'}nen PIN kodunu a{'\u015f'}a{'\u011f'}{'\u0131'}ya girin</li>
              </ol>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">PIN Kodu</Label>
              <Input
                value={pin}
                onChange={e => setPin(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1 text-center text-2xl font-jetbrains tracking-[0.3em]"
                placeholder="1234567"
                maxLength={10}
                data-testid="pin-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinDialog(false)} className="border-[#262626] text-[#A3A3A3]">
              {'\u0130'}ptal
            </Button>
            <Button onClick={handlePinSubmit} disabled={pinLoading || !pin.trim()} className="bg-white text-black hover:bg-[#E5E5E5]" data-testid="submit-pin-btn">
              {pinLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
              Do{'\u011f'}rula
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white flex items-center gap-2">
              <Calendar size={20} /> G{'\u00f6'}nderiyi Zamanla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] p-3">
              <p className="text-sm text-white font-ibm">{tweetText}</p>
              <p className="text-xs text-[#525252] font-jetbrains mt-1">{tweetText.length}/280 karakter</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Tarih</Label>
                <Input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="schedule-date" />
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Saat</Label>
                <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="schedule-time" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)} className="border-[#262626] text-[#A3A3A3]">{'\u0130'}ptal</Button>
            <Button onClick={handleSchedule} disabled={scheduling} className="bg-white text-black hover:bg-[#E5E5E5]" data-testid="confirm-schedule-btn">
              {scheduling ? <Loader2 size={14} className="animate-spin mr-1" /> : <Clock size={14} className="mr-1" />}
              Planla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
